// Import + Brand dialogs for the deck preview toolbar.
//
// - ImportDialog: pick a source format (PDF / PPTX / DOCX / Excel /
//   URL), upload it (or paste the URL), dispatch the corresponding
//   system talent (pdf-to-md, pptx-to-md, doc-to-md, excel-to-md,
//   web-to-md), surface the run result. The agent reads the imported
//   markdown via the deck's context files on the next turn.
//
// - BrandDialog: paste a URL or upload a logo, run brand-extract,
//   show a palette + typography preview, save the brand_profile.json
//   into the deck's memory section so composePrompt picks it up.

import React, { useState } from 'react';
import { X, UploadSimple, Globe, SpinnerGap, CheckCircle, Palette } from '@phosphor-icons/react';
import { runTalent, uploadContextFile } from '../api/client';

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'var(--ds-accent-soft)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const dialogStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #fff)', borderRadius: 12,
  width: 'min(560px, 92vw)', boxShadow: '0 16px 48px var(--ds-shadow-color)',
  padding: 24, fontFamily: 'inherit', color: 'var(--text-primary, #0F172A)',
  maxHeight: '88vh', overflow: 'auto',
};
const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  marginBottom: 18,
};
const btnStyle: React.CSSProperties = {
  padding: '8px 12px', border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 8, background: 'var(--bg-surface, #fff)',
  cursor: 'pointer', fontSize: 13, color: 'var(--text-primary, #0F172A)',
  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle, background: 'var(--ds-accent)', color: 'var(--ds-on-accent)', borderColor: 'var(--ds-accent)',
  fontWeight: 600,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
  background: 'var(--bg-surface, #fff)', color: 'var(--text-primary, #0F172A)',
  boxSizing: 'border-box',
};
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, padding: 12,
  marginBottom: 10,
};

type ImportSource = 'pdf' | 'pptx' | 'docx' | 'xlsx' | 'url';

const TALENT_BY_SOURCE: Record<ImportSource, string> = {
  pdf: 'pdf-to-md',
  pptx: 'pptx-to-md',
  docx: 'doc-to-md',
  xlsx: 'excel-to-md',
  url: 'web-to-md',
};

const SOURCE_LABEL: Record<ImportSource, string> = {
  pdf: 'PDF',
  pptx: 'PowerPoint',
  docx: 'Word / HTML / EPUB',
  xlsx: 'Excel',
  url: 'Web URL',
};

const SOURCE_ACCEPT: Record<Exclude<ImportSource, 'url'>, string> = {
  pdf: '.pdf',
  pptx: '.pptx,.ppt',
  docx: '.docx,.doc,.html,.htm,.epub,.ipynb',
  xlsx: '.xlsx,.xlsm,.xls',
};

interface RunSummary {
  exit_code: number;
  duration_ms: number;
  stdout: string;
  stderr: string;
  outputs: { path: string; size: number; r2_key: string }[];
}

export function ImportDialog({
  deckId,
  onClose,
  onImported,
}: {
  deckId: string;
  onClose: () => void;
  onImported: (summary: { source: ImportSource; talent_slug: string; result: RunSummary }) => void;
}) {
  const [source, setSource] = useState<ImportSource>('pdf');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunSummary | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const dispatch = async (talent_slug: string, args: Record<string, string>, input_keys: string[]) => {
    setBusy(true);
    setError(null);
    try {
      const res = await runTalent(deckId, { talent_slug, args, input_keys });
      const summary: RunSummary = {
        exit_code: res.exit_code,
        duration_ms: res.duration_ms,
        stdout: res.stdout_excerpt,
        stderr: res.stderr_excerpt,
        outputs: res.outputs.map((o) => ({ path: o.path, size: o.size, r2_key: o.r2_key })),
      };
      setResult(summary);
      onImported({ source, talent_slug, result: summary });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const up = await uploadContextFile(deckId, file, 'context');
      await dispatch(TALENT_BY_SOURCE[source], {}, [up.r2_key]);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const handleUrl = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError('URL is required.');
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setError('URL must start with http:// or https://');
      return;
    }
    void dispatch('web-to-md', { url: trimmed }, []);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Import source</h2>
          <button onClick={onClose} style={{ ...btnStyle, padding: 6 }} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 18 }}>
          {(Object.keys(SOURCE_LABEL) as ImportSource[]).map((s) => (
            <button
              key={s}
              onClick={() => setSource(s)}
              style={{
                ...btnStyle,
                justifyContent: 'center',
                padding: '10px 6px',
                fontSize: 12,
                background: source === s ? 'var(--ds-accent-soft)' : 'var(--bg-surface, #fff)',
                borderColor: source === s ? 'var(--ds-accent)' : 'var(--border, #e2e8f0)',
                color: source === s ? 'var(--ds-accent)' : 'var(--text-primary, #0F172A)',
                fontWeight: source === s ? 600 : 400,
              }}
            >
              {SOURCE_LABEL[s]}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)', marginBottom: 4 }}>
            Talent: <code>{TALENT_BY_SOURCE[source]}</code>
          </div>
          {source === 'url' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="url"
                placeholder="https://example.com/article"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                style={inputStyle}
                disabled={busy}
              />
              <button
                onClick={handleUrl}
                disabled={busy || !url.trim()}
                style={{ ...primaryBtnStyle, opacity: busy || !url.trim() ? 0.5 : 1 }}
              >
                {busy ? <SpinnerGap size={14} /> : <Globe size={14} />} Fetch
              </button>
            </div>
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={SOURCE_ACCEPT[source]}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                style={{ ...primaryBtnStyle, opacity: busy ? 0.5 : 1 }}
              >
                {busy ? <SpinnerGap size={14} /> : <UploadSimple size={14} />} Choose file
              </button>
            </div>
          )}
        </div>

        {error && (
          <div style={{ ...cardStyle, borderColor: 'var(--ds-danger-soft)', background: 'var(--ds-danger-soft)', color: 'var(--ds-danger)' }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ ...cardStyle, borderColor: result.exit_code === 0 ? 'var(--ds-success-soft)' : 'var(--ds-danger-soft)', background: result.exit_code === 0 ? 'var(--ds-success-soft)' : 'var(--ds-danger-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 6 }}>
              {result.exit_code === 0 ? <CheckCircle size={16} weight="fill" color="var(--ds-success)" /> : <X size={16} color="var(--ds-danger)" />}
              {result.exit_code === 0 ? 'Imported' : `Failed (exit ${result.exit_code})`}
              <span style={{ fontWeight: 400, color: 'var(--text-muted, #64748b)', fontSize: 11, marginLeft: 4 }}>
                {result.duration_ms} ms
              </span>
            </div>
            {result.outputs.length > 0 && (
              <div style={{ fontSize: 12 }}>
                <div style={{ color: 'var(--text-muted, #64748b)', marginBottom: 4 }}>
                  {result.outputs.length} output file{result.outputs.length === 1 ? '' : 's'}:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.outputs.slice(0, 6).map((o) => (
                    <li key={o.path}>
                      <code>{o.path}</code> <span style={{ color: 'var(--text-muted, #64748b)' }}>({o.size} B)</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.stderr && (
              <pre style={{ fontSize: 11, marginTop: 8, padding: 8, background: 'var(--ds-surface)', borderRadius: 4, maxHeight: 100, overflow: 'auto' }}>
                {result.stderr.slice(0, 600)}
              </pre>
            )}
          </div>
        )}

        <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 12 }}>
          Imported markdown lands in the deck's context files. Mention it in chat or click "Use in chat" to redraft.
        </div>
      </div>
    </div>
  );
}

interface BrandProfile {
  name: string;
  palette: {
    primary: string; secondary: string; accent: string;
    neutral_dark: string; neutral_light: string;
  };
  typography: {
    heading: { family: string; weights: number[] };
    body: { family: string; weights: number[] };
  };
  voice: { tone: string; verbs: string[]; avoid: string[] };
  anchors: { shape_language: string; imagery: string; logo_strategy: string };
}

export function BrandDialog({
  deckId,
  onClose,
  onApplied,
}: {
  deckId: string;
  onClose: () => void;
  onApplied: (profile: BrandProfile) => void;
}) {
  const [mode, setMode] = useState<'url' | 'image'>('url');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const fetchProfile = async (args: Record<string, string>, input_keys: string[]) => {
    setBusy(true);
    setError(null);
    try {
      const r = await runTalent(deckId, { talent_slug: 'brand-extract', args, input_keys });
      if (r.exit_code !== 0) {
        setError(`brand-extract failed (exit ${r.exit_code}): ${r.stderr_excerpt.slice(0, 240)}`);
        return;
      }
      // brand-extract prints the profile JSON to stdout; we parse it
      // directly so the frontend doesn't need a second round-trip to
      // fetch the artefact from R2.
      let parsed: BrandProfile;
      try {
        parsed = JSON.parse(r.stdout_excerpt.trim()) as BrandProfile;
      } catch (e) {
        setError(`brand-extract stdout is not valid JSON: ${(e as Error).message}`);
        return;
      }
      setProfile(parsed);
      onApplied(parsed);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleUrl = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError('URL is required.');
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setError('URL must start with http:// or https://');
      return;
    }
    void fetchProfile({ url: trimmed }, []);
  };

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const up = await uploadContextFile(deckId, file, 'memory');
      await fetchProfile({ image: file.name }, [up.r2_key]);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Palette size={18} weight="duotone" /> Brand profile
          </h2>
          <button onClick={onClose} style={{ ...btnStyle, padding: 6 }} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {(['url', 'image'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                ...btnStyle,
                background: mode === m ? 'var(--ds-accent-soft)' : 'var(--bg-surface, #fff)',
                borderColor: mode === m ? 'var(--ds-accent)' : 'var(--border, #e2e8f0)',
                color: mode === m ? 'var(--ds-accent)' : 'var(--text-primary, #0F172A)',
                fontWeight: mode === m ? 600 : 400,
              }}
            >
              {m === 'url' ? 'From website URL' : 'From logo / screenshot'}
            </button>
          ))}
        </div>

        {mode === 'url' ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input
              type="url"
              placeholder="https://your-brand.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              style={inputStyle}
              disabled={busy}
            />
            <button
              onClick={handleUrl}
              disabled={busy || !url.trim()}
              style={{ ...primaryBtnStyle, opacity: busy || !url.trim() ? 0.5 : 1 }}
            >
              {busy ? <SpinnerGap size={14} /> : 'Analyse'}
            </button>
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg,.webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              style={{ ...primaryBtnStyle, opacity: busy ? 0.5 : 1 }}
            >
              {busy ? <SpinnerGap size={14} /> : <UploadSimple size={14} />} Choose logo / screenshot
            </button>
          </div>
        )}

        {error && (
          <div style={{ ...cardStyle, borderColor: 'var(--ds-danger-soft)', background: 'var(--ds-danger-soft)', color: 'var(--ds-danger)' }}>
            {error}
          </div>
        )}

        {profile && (
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{profile.name}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {Object.entries(profile.palette).map(([k, v]) => (
                <div key={k} title={`${k}: ${v}`} style={{ flex: 1 }}>
                  <div style={{ height: 36, background: v, borderRadius: 6, border: '1px solid var(--ds-surface-subtle)' }} />
                  <div style={{ fontSize: 10, color: 'var(--text-muted, #64748b)', marginTop: 4, textAlign: 'center' }}>
                    {v}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
              <div>Heading: <code>{profile.typography.heading.family}</code></div>
              <div>Body: <code>{profile.typography.body.family}</code></div>
            </div>
          </div>
        )}

        <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 12 }}>
          The brand profile is saved into the deck's <code>memory/</code> section so the next chat turn picks it up automatically.
        </div>
      </div>
    </div>
  );
}
